/**
 * Grouping of the objective function's terms into "tables".
 *
 * The objective is a linear combination of variables (coefficient × variable).
 * For each term we expose its cost coefficient (an input to the solver) and,
 * joining with the solution, the variable's value and the resulting cost
 * contribution (coefficient × value).
 *
 * The buckets are the authored **cost categories** when the model carries them
 * (`objective.terms`), so each table holds exactly the rows one editable
 * category produced. A model built without named categories falls back to
 * bucketing by the variable's own `group` ("P_gen"), which is the same split
 * whenever each category covers one variable class.
 */

import { Model, HighsRawSolution, IndexValue, Variable } from './highs';

/** One objective term: its index values, coefficient, and solved cost. */
export interface ObjectiveTermRow {
  /** Underlying variable name (used as a stable React key). */
  name: string;
  /** Index column values, keyed by column name. */
  indices: Record<string, IndexValue>;
  /** Cost coefficient on this variable in the objective. */
  coefficient: number;
  /** Solved variable value, or undefined if absent from the solution. */
  value?: number;
  /** Cost contribution: coefficient × value (undefined if value is unknown). */
  cost?: number;
}

/** A group of objective terms sharing the same index columns, e.g. "P_gen". */
export interface ObjectiveTable {
  name: string;
  columns: string[];
  rows: ObjectiveTermRow[];
}

/** Fallback group and column for terms whose variable has no grouping metadata. */
const UNGROUPED_TABLE = 'other';
const UNGROUPED_COLUMN = 'variable';
/**
 * Leading column naming the variable class, added only when one cost category
 * spans several of them — without it, the charge and discharge rows of a
 * battery-cycling category would be two identical-looking (battery, timestep)
 * rows.
 */
const CLASS_COLUMN = 'class';

/**
 * Group the objective's terms into tables — one per cost category, or per
 * variable class for a model with no named categories — joining each with the
 * solution to compute its cost contribution. Only variables with a nonzero
 * objective coefficient appear.
 */
export function extractObjectiveTables(
  model: Model,
  solution?: HighsRawSolution
): ObjectiveTable[] {
  const byName = new Map<string, Variable>();
  for (const variable of model.variables) {
    byName.set(variable.name, variable);
  }

  const terms = model.objective.terms;
  if (!terms) return groupedByVariableClass(model, byName, solution);

  return terms
    .map((term) => buildTable(term.name, term.coefficients, byName, solution))
    .filter((table) => table.rows.length > 0);
}

/** One table from one coefficient map, with the rows sorted by their index columns. */
function buildTable(
  name: string,
  coefficients: Map<string, number>,
  byName: Map<string, Variable>,
  solution?: HighsRawSolution
): ObjectiveTable {
  const table: ObjectiveTable = { name, columns: [], rows: [] };
  const values = solution?.Columns;
  // Each row's variable class, kept alongside so the class column can be added
  // afterwards — it's only worth showing once we know the category spans more
  // than one, and until then it would be noise in every row's indices.
  const classOf = new Map<ObjectiveTermRow, string>();

  coefficients.forEach((coefficient, varName) => {
    if (coefficient === 0) return;

    const variable = byName.get(varName);
    const grouped = variable?.group !== undefined && variable.indices;
    const indices: Record<string, IndexValue> = grouped
      ? { ...variable!.indices! }
      : { [UNGROUPED_COLUMN]: varName };

    for (const column of Object.keys(indices)) {
      if (!table.columns.includes(column)) table.columns.push(column);
    }

    const value = values?.[varName]?.Primal;
    const row: ObjectiveTermRow = {
      name: varName,
      indices,
      coefficient,
      value,
      cost: value !== undefined ? coefficient * value : undefined,
    };
    classOf.set(row, variable?.group ?? UNGROUPED_TABLE);
    table.rows.push(row);
  });

  if (new Set(classOf.values()).size > 1) {
    table.columns.unshift(CLASS_COLUMN);
    for (const row of table.rows) {
      row.indices = { [CLASS_COLUMN]: classOf.get(row)!, ...row.indices };
    }
  }

  table.rows.sort((a, b) => compareByColumns(a.indices, b.indices, table.columns));
  return table;
}

/** The pre-cost-category split: one table per variable class. */
function groupedByVariableClass(
  model: Model,
  byName: Map<string, Variable>,
  solution?: HighsRawSolution
): ObjectiveTable[] {
  // Preserve first-seen order of the groups, then build each from its own slice.
  const groups = new Map<string, Map<string, number>>();
  model.objective.coefficients.forEach((coefficient, varName) => {
    if (coefficient === 0) return;
    const group = byName.get(varName)?.group ?? UNGROUPED_TABLE;
    const slice = groups.get(group) ?? new Map<string, number>();
    slice.set(varName, coefficient);
    groups.set(group, slice);
  });

  return Array.from(groups, ([name, coefficients]) => buildTable(name, coefficients, byName, solution));
}

/** Order rows by their index columns: numbers numerically, everything else lexically. */
function compareByColumns(
  a: Record<string, IndexValue>,
  b: Record<string, IndexValue>,
  columns: string[]
): number {
  for (const column of columns) {
    const av = a[column];
    const bv = b[column];
    if (av === bv) continue;
    if (typeof av === 'number' && typeof bv === 'number') {
      return av - bv;
    }
    return String(av ?? '').localeCompare(String(bv ?? ''));
  }
  return 0;
}
